import { computed, effect, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { CalendarWeek, TimeSpanInput } from '@fmnts/core/chronos';
import * as Arr from 'effect/Array';
import * as Fn from 'effect/Function';
import moment from 'moment';
import { combineLatest, map, OperatorFunction, shareReplay } from 'rxjs';
import { LocaleService } from './locale.service';

type FirstDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const isValidFirstDayOfWeek = (value: number): value is FirstDayOfWeek =>
  value >= 0 && value <= 6;

@Injectable()
export class DateService {
  readonly localeId = inject(LocaleService).localeId;
  readonly #locale$ = toObservable(this.localeId).pipe(shareReplay(1));
  /** Locale for moment.js */
  readonly #momentLocale = this.localeId;

  readonly #dateFormat = computed(() =>
    moment.localeData(this.#momentLocale()).longDateFormat('L'),
  );

  /**
   * Emits with the localized date format for inputs, which ensures to
   * use the padded versions of formats, so e.g. the format `D-M-YYYY`
   * becomes `DD-MM-YYYY`.
   */
  readonly dateInputFormat = computed(() =>
    toDateInputFormat(this.#dateFormat()),
  );

  readonly placeholder = computed(() =>
    transformFormatToPlaceholder(this.#dateFormat(), this.localeId()),
  );

  /** Day that the week starts on. */
  readonly weekStartsOn = computed(() => {
    const firstDayOfWeek = moment
      .localeData(this.#momentLocale())
      .firstDayOfWeek();
    return isValidFirstDayOfWeek(firstDayOfWeek) ? firstDayOfWeek : 0;
  });

  constructor() {
    // Updates the locale internally maintained by moment.js
    effect(() => {
      moment.locale(this.#momentLocale());
    });
  }

  /**
   * Can be used within `provideAppInitializer`
   * to instantiate an instance eaterly
   **/
  public init(): void {
    return;
  }

  /**
   * Using this ensures that the returned moment object
   * uses the correct locale.
   *
   * @returns
   * A new moment object
   */
  public moment(
    input: moment.MomentInput,
    format?: moment.MomentFormatSpecification,
    strict?: boolean,
  ): moment.Moment {
    return moment(input, format, strict).locale(this.#momentLocale());
  }

  /**
   * @returns
   * Creates a localized moment object from each value
   * emitted by the source Observable and emits them.
   */
  public moment$(): OperatorFunction<moment.MomentInput, moment.Moment> {
    return (obs$) =>
      combineLatest([obs$, this.#locale$]).pipe(map(([s]) => this.moment(s)));
  }

  /**
   * @param date Date that's inside the week to construct
   *
   * @returns
   * An array of all weekdays for the week of the given date.
   */
  public weekdays(date: moment.MomentInput): moment.Moment[] {
    const d = this.moment(date);
    return [0, 1, 2, 3, 4, 5, 6].map((day) => d.clone().weekday(day));
  }

  /**
   * @param date Date which lies inside the calendar week to construct
   *
   * @returns
   * The calendar week for a given date.
   */
  public calendarWeek(date: moment.MomentInput): CalendarWeek {
    const week = this.moment(date);
    return {
      weekNumber: week.week(),
      days: Fn.pipe(
        this.weekdays(week),
        Arr.map((d) => d.clone().startOf('day')),
      ),
    };
  }

  /**
   * @param range Date range.
   *
   * @returns
   * An Array of all calendar weeks within the given range, using the current
   * locale.
   */
  public calendarWeeksForRange(range: TimeSpanInput): CalendarWeek[] {
    const [startInput, endInput] = range;

    const start = this.moment(startInput).clone().startOf('day');
    const end = this.moment(endInput).clone().startOf('day');

    const calendarWeeks: CalendarWeek[] = [];
    const week = start.weekday(0);
    while (week.isBefore(end, 'day')) {
      calendarWeeks.push(this.calendarWeek(week));
      week.add(1, 'week');
    }

    return calendarWeeks;
  }
}

function localeData(): {
  [lang: string]: { y: string; m: string; d: string };
} {
  return {
    sv: { y: 'Å', m: 'M', d: 'D' },
    'sv-SE': { y: 'Å', m: 'M', d: 'D' },
    de: { y: 'J', m: 'M', d: 'D' },
    'de-AT': { y: 'J', m: 'M', d: 'D' },
    'de-CH': { y: 'J', m: 'M', d: 'D' },
    fr: { y: 'A', m: 'M', d: 'J' },
    es: { y: 'A', m: 'M', d: 'D' },
    it: { y: 'A', m: 'M', d: 'G' },
  };
}

function transformFormatToPlaceholder(format: string, locale: string): string {
  const dateMapping = localeData()[locale];
  return dateMapping
    ? format
        .replace(/Y/g, dateMapping.y)
        .replace(/M/g, dateMapping.m)
        .replace(/D/g, dateMapping.d)
    : format;
}

function toDateInputFormat(format: string): string {
  return format
    .replace(/[D]+/i, 'DD')
    .replace(/[M]+/i, 'MM')
    .replace(/[Y]+/i, 'YYYY');
}
